import React, { useState, useEffect } from 'react'
import { bool, arrayOf, shape, string } from 'prop-types'
import classnames from 'classnames'

import isDOMAvailable from 'utils/isDOMAvailable'

import SectionContainer from 'components/atoms/SectionContainer'
import Container from 'components/atoms/Container'
import Typography from 'components/atoms/Typography'
import Markdown from 'components/atoms/MarkdownContent'
import Button from 'components/atoms/Button'
import Paper from 'components/atoms/Paper'
import ScholarshipRegistrationButton from 'components/molecules/ScholarshipRegistrationButton'
import SectionTitle from 'components/atoms/SectionTitle'

import { SVGIcon } from 'components/qstrap'

const RegistrationStep = ({ title, description }) => (
  <div id="registration" className="registration-step">
    <div className="registration-step__line"/>
    <div className="registration-step__tl-card">
      <Paper rounded shadowed>
        <div className="registration-step__tl-content">
          <Typography
            component="h3"
            variant="body"
            className="registration-step__title"
            fontWeight="bold"
          >
            {title}
          </Typography>
          <Typography
            component="p"
            variant="subbody"
          >
            {description}
          </Typography>
        </div>
      </Paper>
    </div>
    <style jsx>
      {`
        .registration-step {
          position: relative;
          min-width: 90%;
          color: #0c2846 !important;
          @media screen and (min-width: 768px) {
            min-width: 33.33333%;
          }
          &:first-child {
            .registration-step__line {
              border-top-left-radius: 8px;
              border-bottom-left-radius: 8px;
            }
          }
          &:last-child {
            .registration-step__line {
              border-top-right-radius: 8px;
              border-bottom-right-radius: 8px;
            }
          }
          :global(&__title) {
            text-align: center;
            color: #0c2846;
            @media screen and (min-width: 768px) {
              font-size: 20px;
            }
          }
          &__line {
            display: inline-block;
            width: 100%;
            height: 8px;
            margin: 20px 0;
            background-color: #0fc3c4;
          }
          &__tl-card {
            margin: 16px 8px;
            &::before {
              content: "";
              display: block;
              width: 40px;
              border-left: 20px solid transparent;
              border-right: 20px solid transparent;
              border-bottom: 20px solid white;
              margin-top: 0px;
              margin-left: auto;
              margin-right: auto;
              z-index: 2;
            }
          }
          &__tl-content {
            width: 100%;
            padding: 32px 24px;
          }
          &::before {
            content: "";
            display: block;
            position: absolute;
            left: 50%;
            width: 48px;
            height: 48px;
            border: solid #fff 14px;
            background: #0fc3c4;
            box-shadow: 0 6px 16px 0 rgba(107, 143, 188, 0.1);
            border-radius: 100%;
            margin-left: -28px;
          }
          :global(.text-variant-subbody) {
            color: #0c2846 !important;
          }
        }
      `}
    </style>
  </div>
)

const ScholarshipRegistration = ({ title, description, steps, paid, hasTriggered, setHasTriggered }) => {
  const [collapsed, setCollapsed] = useState(true)
  const [activeStepIdx, setActiveStepIdx] = useState(0)
  const [showNext, setShowNext] = useState(true)
  const activeToggle = description.length > 977
  const collapseableClass = classnames('scholarship-registration__description', {
    collapsed: collapsed && activeToggle,
  })

  const toggleCollapse = () => setCollapsed(!collapsed)

  useEffect(() => {
    if (isDOMAvailable && steps.length > 0) {
      const stepsCount = steps.length
      const regStepsEl = document.querySelector('.scholarship-registration__steps')
      const regStepsWidth = document.querySelector('.registration-step').offsetWidth
      const newShowNextState = (regStepsEl.offsetWidth + (regStepsWidth * activeStepIdx)) <= (regStepsWidth * stepsCount - 10)

      if (newShowNextState !== showNext) {
        setShowNext(!showNext)
      }

      regStepsEl && regStepsEl.scrollTo({ left: activeStepIdx * regStepsWidth, behavior: 'smooth' })
    }
  }, [activeStepIdx])

  return (
    <div className="scholarship-registration">
      <SectionContainer
        paddingTop={120}
        backgroundColor="#f6f7fa"
      >
        <Container>
          <SectionTitle title={title || 'Info Pendaftaran'} />
          {
            steps.length > 0 &&
            <div
              id="steps-container"
              className="scholarship-registration__steps-container"
            >
              <div className="scholarship-registration__steps">
                {
                  steps.map(step => (
                    <RegistrationStep
                      title={step.title}
                      description={step.description}
                      key={`reg-step-${step.id}`}
                    />
                  ))
                }
              </div>
              <div className="scholarship-registration__steps-control">
                {
                  activeStepIdx > 0 &&
                  <button
                    type="button"
                    onClick={() => setActiveStepIdx(activeStepIdx - 1)}
                  >
                    <SVGIcon size="md" name="back" />
                  </button>
                }
                {
                  showNext &&
                  <button
                    type="button"
                    onClick={() => setActiveStepIdx(activeStepIdx + 1)}
                  >
                    <SVGIcon size="md" name="next" />
                  </button>
                }
              </div>
            </div>
          }
          {paid &&
            <div className="scholarship-registration__action">
              <ScholarshipRegistrationButton hasTriggered={hasTriggered} setHasTriggered={setHasTriggered}/>
            </div>
          }
          { description && <Markdown className={collapseableClass} content={description} /> }
          { activeToggle &&
            <div className="scholarship-registration__action">
              <Button
                className="scholarship-registration__toggle-btn"
                component="button"
                variant="secondary"
                onClick={toggleCollapse}
              >
                {
                  collapsed ? 'Baca Selengkapnya . . .' : 'Sembunyikan'
                }
              </Button>
            </div>
          }
        </Container>
      </SectionContainer>
      <style jsx>
        {`
          .scholarship-registration {
            &__action {
              padding: 0 0 32px;
              text-align: center;
            }
            &__steps-container {
              position: relative;
              width: 100%;
            }
            &__steps {
              position: relative;
              display: flex;
              flex-wrap: nowrap;
              overflow: hidden;
              margin-bottom: 80px;
            }
            &__steps-control {
              margin-top: -48px;
              margin-bottom: 32px;
              text-align: center;
              button {
                width: 48px;
                height: 48px;
                background-color: #0fc3c4;
                border: none;
                border-radius: 100%;
                color: #fff;
                margin: 8px;
              }
              @media screen and (min-width: 768px) {
                text-align: end;
                margin-right: 32px;
              }
            }
            :global(&__toggle-btn) {
              background-color: transparent;
              border: none;
              font-weight: bold;
              font-size: 16px;
              &:hover{
                background-color: transparent;
                text-decoration: underline;
              }
            }
            :global(&__description) {
              max-height: 3000px;
              transition: max-height 0.3s ease-out;
            }
            :global(&__description.collapsed) {
              transition: max-height 1s ease-out;
              max-height: 360px;
              overflow: hidden;
              position: relative;
              &::after {
                content: "";
                position: absolute;
                bottom: 0;
                width: 100%;
                height: 48px;
                background-image: linear-gradient(
                  to bottom, rgba(255, 255, 255, 0), #f6f7fa
                );
              }
            }
          }
        `}
      </style>
    </div>
  )
}

const registrationStepShape = shape({
  id: string,
  title: string,
  description: string,
})

ScholarshipRegistration.propsTypes = {
  title: string,
  description: string,
  paid: bool,
  steps: arrayOf(registrationStepShape),
}

ScholarshipRegistration.defaultProps = {
  title: 'Info Pendaftaran',
}

export default ScholarshipRegistration
